import { Button, Pagination, Skeleton, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSelector } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import table from '../materials/Materials.module.css';
import classes from './Purchasing.module.css';
import type { SortDirection } from './requisitions.js';

/**
 * Satın alma tablolarının ortak parçaları (M6f plan §0.3, §0.6; CLAUDE.md "tablolar sıralanabilir
 * ve sayfalanır"): sıralanabilir başlık, sayfalama, hata, eşleşme yok ve yükleniyor iskeleti.
 */

export interface SortState<K extends string> {
  key: K;
  dir: SortDirection;
}

/** Sütunun yeni sıralaması: aynı sütunda yön döner, başka sütunda artan başlar. */
export function nextSort<K extends string>(sort: SortState<K>, column: K): SortState<K> {
  return sort.key === column
    ? { key: column, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
    : { key: column, dir: 'asc' };
}

/** Sıralanabilir sütun başlığı: `aria-sort` `<th>` üzerinde (M6f §0.6); düğmeyle iki yön. */
export function SortableTh<K extends string>({
  column,
  label,
  sort,
  onSort,
  numeric,
  className,
  drop,
}: {
  column: K;
  label: string;
  sort: SortState<K>;
  onSort: (sort: SortState<K>) => void;
  numeric?: boolean;
  className?: string;
  /** Dar tabloda gizlenme sırası (`fit-columns.ts`); 1 önce gizlenir. */
  drop?: number | undefined;
}) {
  const { t } = useTranslation();
  const active = sort.key === column;
  const Icon = !active ? IconSelector : sort.dir === 'asc' ? IconChevronUp : IconChevronDown;
  return (
    <th
      scope="col"
      className={className}
      data-numeric={numeric || undefined}
      data-drop={drop}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <UnstyledButton
        className={classes.sortButton}
        onClick={() => onSort(nextSort(sort, column))}
        aria-label={t('purchasing.table.sortBy', { column: label })}
      >
        <span>{label}</span>
        <Icon size={14} aria-hidden />
      </UnstyledButton>
    </th>
  );
}

/** "Showing x–y of n" ve sayfalama; tek sayfalık tabloda sayfalama gizlenir (M6f §0.6). */
export function TablePager({
  page,
  pages,
  summary,
  label,
  onPage,
}: {
  page: number;
  pages: number;
  summary: string;
  label: string;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={table.pager}>
      <span>{summary}</span>
      {pages > 1 ? (
        <Pagination
          total={pages}
          value={page}
          onChange={onPage}
          size="sm"
          aria-label={label}
          getControlProps={(control) => ({ 'aria-label': t(`materials.page.${control}`) })}
          getItemProps={(number) => ({
            'aria-label': t('materials.page.number', { page: number }),
          })}
        />
      ) : null}
    </div>
  );
}

/** Veri okunamadı: hata kodu ve "Try again" (M6f §0.6: veri yüklenmeden "kayıt yok" denmez). */
export function LoadError({
  code,
  onRetry,
  title,
}: {
  code: string;
  onRetry: () => void;
  title?: string;
}) {
  const { t } = useTranslation();
  const known = t(`purchasing.errors.${code}`, { defaultValue: '' });
  return (
    <EmptyState
      tone="danger"
      title={title ?? t('common.loadError')}
      description={
        <>
          {known ? `${known} ` : null}
          <code className={classes.errorCode}>{code}</code>
        </>
      }
      action={
        <Button size="xs" variant="light" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      }
    />
  );
}

/** Süzgece uyan kayıt yok: "Clear filters". */
export function NoMatch({ title, onClear }: { title: string; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      title={title}
      action={
        <Button size="xs" variant="light" onClick={onClear}>
          {t('common.clearFilters')}
        </Button>
      }
    />
  );
}

/** Yükleniyor: tablonun şeklinde iskelet (başlık satırı ve satırlar; M6f §0.6). */
export function TableSkeleton({
  columns,
  rows = 6,
  label,
}: {
  columns: number;
  rows?: number;
  label: string;
}) {
  const cells = Array.from({ length: columns }, (_, index) => index);
  return (
    <section className={table.tableWrap} aria-busy="true" aria-label={label}>
      <table className={`${table.table} ${classes.table}`}>
        <thead>
          <tr>
            {cells.map((cell) => (
              <th key={cell} scope="col">
                <Skeleton height={10} width="60%" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: iskelet satırlarının kimliği yok
            <tr key={row}>
              {cells.map((cell) => (
                <td key={cell}>
                  <Skeleton height={12} width={cell === 0 ? '40%' : '75%'} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
